import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles } from 'lucide-react';

// DiceBear 头像风格配置 - 孩子们喜欢的卡通形象
const AVATAR_STYLES = [
  { id: 'bottts', name: '机器人', color: 'e0e7ff' },
  { id: 'avataaars', name: '卡通人物', color: 'ffe4e6' },
  { id: 'fun-emoji', name: '趣味表情', color: 'fef3c7' },
  { id: 'adventurer', name: '冒险家', color: 'dbeafe' },
  { id: 'micah', name: '可爱男孩', color: 'd1fae5' },
  { id: 'big-smile', name: '开心笑脸', color: 'fce7f3' },
  { id: 'personas', name: '个性角色', color: 'e0e7ff' },
  { id: 'lorelei', name: '童话精灵', color: 'ede9fe' },
  { id: 'notionists', name: '简约风', color: 'f0fdf4' },
  { id: 'open-peeps', name: '手绘风', color: 'fff7ed' },
  { id: 'pixel-art', name: '像素风', color: 'ecfccb' },
  { id: 'shapes', name: '几何图形', color: 'cffafe' },
];

// 每个风格的预设种子
const STYLE_SEEDS: Record<string, string[]> = {
  bottts: ['robot1', 'robot2', 'robot3', 'cyber1', 'tech2', 'mecha3', 'bot4', 'droid5'],
  avataaars: ['boy1', 'girl2', 'kid3', 'child4', 'teen5', 'young6', 'happy7', 'cool8'],
  'fun-emoji': ['smile1', 'laugh2', 'wink3', 'star4', 'heart5', 'party6', 'cool7', 'cute8'],
  adventurer: ['hero1', 'knight2', 'wizard3', 'pirate4', 'ninja5', 'pilot6', 'sailor7', 'scout8'],
  micah: ['mike1', 'tom2', 'jack3', 'sam4', 'alex5', 'max6', 'leo7', 'ben8'],
  'big-smile': ['joy1', 'grin2', 'beam3', 'glow4', 'shine5', 'bright6', 'sunny7', 'happy8'],
  personas: ['art1', 'music2', 'sport3', 'book4', 'game5', 'dance6', 'cook7', 'draw8'],
  lorelei: ['fairy1', 'elf2', 'sprite3', 'pixie4', 'angel5', 'star6', 'moon7', 'sun8'],
  notionists: ['simple1', 'clean2', 'minimal3', 'pure4', 'soft5', 'light6', 'calm7', 'zen8'],
  'open-peeps': ['sketch1', 'doodle2', 'line3', 'hand4', 'draw5', 'art6', 'pen7', 'ink8'],
  'pixel-art': ['retro1', 'game2', 'bit3', 'arcade4', 'classic5', 'vintage6', 'old7', '8bit8'],
  shapes: ['circle1', 'square2', 'triangle3', 'hex4', 'star5', 'diamond6', 'oval7', 'rect8'],
};

interface AvatarSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSeed: string;
  onSelect: (seed: string, style: string) => void;
}

export default function AvatarSelectorModal({ isOpen, onClose, currentSeed, onSelect }: AvatarSelectorModalProps) {
  const [selectedStyle, setSelectedStyle] = useState<string>('bottts');
  const [hoveredSeed, setHoveredSeed] = useState<string | null>(null);

  // 解析当前种子获取风格和实际种子
  const parseCurrentSeed = () => {
    if (!currentSeed) return { style: 'bottts', seed: '' };
    const parts = currentSeed.split(':');
    if (parts.length === 2) {
      return { style: parts[0], seed: parts[1] };
    }
    return { style: 'bottts', seed: currentSeed };
  };

  const current = parseCurrentSeed();

  const getAvatarUrl = (style: string, seed: string, bgColor: string) => {
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&backgroundColor=${bgColor}`;
  };

  const handleSelect = (seed: string) => {
    const styleConfig = AVATAR_STYLES.find(s => s.id === selectedStyle);
    const fullSeed = `${selectedStyle}:${seed}`;
    onSelect(fullSeed, selectedStyle);
    onClose();
  };

  const seeds = STYLE_SEEDS[selectedStyle] || STYLE_SEEDS['bottts'];
  const currentStyleConfig = AVATAR_STYLES.find(s => s.id === selectedStyle);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            {/* 弹窗主体 */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              {/* 头部 */}
              <div className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white">
                    <Sparkles className="w-5 h-5" />
                    <h2 className="text-lg font-bold">选择你的头像</h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {/* 装饰圆点 */}
                <div className="absolute top-2 right-20 w-3 h-3 bg-yellow-300 rounded-full opacity-60"></div>
                <div className="absolute bottom-3 left-10 w-2 h-2 bg-green-300 rounded-full opacity-50"></div>
              </div>

              {/* 风格选择器 */}
              <div className="p-4 border-b border-gray-100">
                <p className="text-xs text-gray-500 mb-2 font-medium">选择风格</p>
                <div className="flex flex-wrap gap-2">
                  {AVATAR_STYLES.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                        selectedStyle === style.id
                          ? 'bg-indigo-500 text-white shadow-md shadow-indigo-200 scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {style.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* 头像网格 */}
              <div className="p-5">
                <div className="grid grid-cols-4 gap-3">
                  {seeds.map((seed, index) => {
                    const isSelected = current.style === selectedStyle && current.seed === seed;
                    const isHovered = hoveredSeed === seed;
                    const avatarUrl = getAvatarUrl(selectedStyle, seed, currentStyleConfig?.color || 'e0e7ff');

                    return (
                      <motion.button
                        key={seed}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => handleSelect(seed)}
                        onMouseEnter={() => setHoveredSeed(seed)}
                        onMouseLeave={() => setHoveredSeed(null)}
                        className={`relative aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-indigo-500 shadow-lg shadow-indigo-200 scale-105'
                            : isHovered
                            ? 'border-indigo-300 shadow-md scale-105'
                            : 'border-gray-100 hover:border-indigo-200'
                        }`}
                      >
                        <img
                          src={avatarUrl}
                          alt={`${selectedStyle} avatar`}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center">
                            <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        )}
                        {isHovered && !isSelected && (
                          <div className="absolute inset-0 bg-indigo-500/10"></div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* 底部提示 */}
              <div className="px-5 pb-4 text-center">
                <p className="text-xs text-gray-400">点击头像即可更换，展现你的个性！</p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
